import { Injectable } from "@angular/core";
import { Subscription, from, fromEvent, merge } from "rxjs";
import { switchMap, map } from "rxjs/operators";
import { GuideNavigatorService } from "../guide-navigator.service";
import { GuideStep } from "../guide-step.model";
import { GuideService } from "../guide.service";
import { GuideArrowSide, GuideOverlayComponent } from "./guide-overlay.component";
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef,
} from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";

/** Ordem de preferência de ancoragem: abaixo, acima, à direita e à esquerda do elemento-alvo. */
const GUIDE_OVERLAY_POSITIONS: ConnectedPosition[] = [
  { originX: "center", originY: "bottom", overlayX: "center", overlayY: "top", offsetY: 14 },
  { originX: "center", originY: "top", overlayX: "center", overlayY: "bottom", offsetY: -14 },
  { originX: "end", originY: "center", overlayX: "start", overlayY: "center", offsetX: 14 },
  { originX: "start", originY: "center", overlayX: "end", overlayY: "center", offsetX: -14 },
];

const SPOTLIGHT_PADDING_PX = 6;
/** Margem da viewport: se o alvo estiver fora disso, o guia rola até ele. */
const VISIBLE_MARGIN_PX = 96;
const SCROLL_SETTLE_TIMEOUT_MS = 700;

/**
 * Liga o GuideService a uma representação visual: assina `currentStep$` e abre/fecha um overlay do
 * CDK ancorado ao elemento-alvo do passo atual. A resolução do alvo (rota + espera no DOM) fica a
 * cargo do GuideNavigatorService.
 *
 * Precisa ser iniciado explicitamente uma vez, via `listen()`, pela shell da aplicação
 * (ver `AdminLayoutComponent`).
 */
@Injectable({
  providedIn: "root",
})
export class GuideOverlayService {
  private overlayRef: OverlayRef | null = null;
  private positionSubscription: Subscription | null = null;
  private outsideClickSubscription: Subscription | null = null;
  private stepPreparation: Subscription | null = null;
  private listening = false;
  /** Spotlight no body — fora do pane do CDK (transform quebra position:fixed). */
  private spotlightEl: HTMLElement | null = null;
  private spotlightTarget: Element | null = null;

  constructor(
    private readonly overlay: Overlay,
    private readonly guideService: GuideService,
    private readonly guideNavigator: GuideNavigatorService
  ) {}

  listen(): void {
    if (this.listening) {
      return;
    }
    this.listening = true;
    this.guideService.currentStep$.subscribe((step) => this.handleStepChange(step));
  }

  private handleStepChange(step: GuideStep | null): void {
    this.stepPreparation?.unsubscribe();
    this.stepPreparation = null;

    if (!step) {
      this.disposeOverlay();
      return;
    }

    if (step.skipIfTargetAbsent && !this.queryTarget(step.targetSelector)) {
      this.guideService.next();
      return;
    }

    this.disposeOverlay();

    const timeoutMs = this.queryTarget(step.targetSelector) ? 3000 : 250;

    this.stepPreparation = this.guideNavigator
      .prepareTarget(step, timeoutMs)
      .pipe(
        switchMap((target) =>
          from(this.ensureTargetVisible(target as HTMLElement)).pipe(map(() => target))
        )
      )
      .subscribe({
        next: (target) => this.openOverlayFor(target, step),
        error: () => {
          this.guideService.next();
        },
      });
  }

  /**
   * Se o ponto de foco do alvo estiver fora da área visível, rola o painel
   * (PerfectScrollbar em `.main-panel`) até centralizá-lo, e só então o balão abre.
   */
  private async ensureTargetVisible(el: HTMLElement): Promise<void> {
    if (this.isFocusVisible(el)) {
      return;
    }
    this.scrollTargetIntoView(el);
    // PerfectScrollbar / layout: dá um frame para o scrollTop aplicar.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await this.waitForScrollSettle(el);
  }

  /** Ponto perto do topo do alvo (balão ancora aí; em blocos altos o centro fica lá embaixo). */
  private focusY(el: HTMLElement): number {
    const rect = el.getBoundingClientRect();
    return rect.top + Math.min(Math.max(rect.height / 2, 0), 120);
  }

  private isFocusVisible(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return false;
    }
    const y = this.focusY(el);
    const mainPanel = document.querySelector(".main-panel") as HTMLElement | null;
    if (
      document.documentElement.classList.contains("perfect-scrollbar-on") &&
      mainPanel &&
      mainPanel.contains(el)
    ) {
      const panelRect = mainPanel.getBoundingClientRect();
      return y >= panelRect.top + VISIBLE_MARGIN_PX && y <= panelRect.bottom - VISIBLE_MARGIN_PX;
    }
    return y >= VISIBLE_MARGIN_PX && y <= window.innerHeight - VISIBLE_MARGIN_PX;
  }

  private scrollTargetIntoView(el: HTMLElement): void {
    const focusOffset = Math.min(Math.max(el.getBoundingClientRect().height / 2, 0), 120);
    const mainPanel = document.querySelector(".main-panel") as HTMLElement | null;
    const perfectScrollbarOn =
      document.documentElement.classList.contains("perfect-scrollbar-on");

    // Desktop Windows/Linux: o scroll real é o `.main-panel` (PerfectScrollbar).
    if (perfectScrollbarOn && mainPanel && mainPanel.contains(el)) {
      const elRect = el.getBoundingClientRect();
      const panelRect = mainPanel.getBoundingClientRect();
      const focusAbsolute =
        elRect.top - panelRect.top + mainPanel.scrollTop + focusOffset;
      mainPanel.scrollTop = Math.max(0, focusAbsolute - mainPanel.clientHeight / 2);
      mainPanel.dispatchEvent(new Event("scroll"));
      return;
    }

    // Mac / mobile / sem PS: scroll da janela.
    const focusY = el.getBoundingClientRect().top + window.scrollY + focusOffset;
    window.scrollTo(0, Math.max(0, focusY - window.innerHeight / 2));
  }

  private waitForScrollSettle(el: HTMLElement): Promise<void> {
    return new Promise((resolve) => {
      let lastTop = el.getBoundingClientRect().top;
      let stableFrames = 0;
      const startedAt = performance.now();

      const tick = () => {
        const top = el.getBoundingClientRect().top;
        if (Math.abs(top - lastTop) < 1.5) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          lastTop = top;
        }
        if (stableFrames >= 2 || performance.now() - startedAt > SCROLL_SETTLE_TIMEOUT_MS) {
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
    });
  }

  private openOverlayFor(target: Element, step: GuideStep): void {
    const centered = step.placement === "center";
    const positionStrategy = centered
      ? this.overlay.position().global().centerHorizontally().centerVertically()
      : this.overlay
          .position()
          .flexibleConnectedTo(target as HTMLElement)
          .withPositions(GUIDE_OVERLAY_POSITIONS)
          .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: "guide-overlay-backdrop",
      // Não bloqueia o scroll da página; só reposiciona o balão ao rolar.
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: "guide-overlay-pane",
    });

    this.mountSpotlight(target);
    this.listenOutsideClick();

    const componentRef = this.overlayRef.attach(new ComponentPortal(GuideOverlayComponent));
    componentRef.instance.step = step;
    componentRef.instance.targetElement = target;

    const layoutEvents$ = merge(
      fromEvent(window, "resize"),
      fromEvent(window, "scroll", { capture: true })
    );

    if (!centered) {
      const flexible = positionStrategy as FlexibleConnectedPositionStrategy;
      this.positionSubscription = new Subscription();
      this.positionSubscription.add(
        flexible.positionChanges.subscribe((change) => {
          componentRef.instance.arrowSide = this.resolveArrowSide(change.connectionPair);
          this.updateSpotlight();
          componentRef.changeDetectorRef.detectChanges();
        })
      );
      this.positionSubscription.add(
        layoutEvents$.subscribe(() => {
          this.updateSpotlight();
          this.overlayRef?.updatePosition();
          componentRef.changeDetectorRef.detectChanges();
        })
      );
    } else {
      componentRef.instance.arrowSide = this.arrowSideTowardTarget(target);
      componentRef.changeDetectorRef.detectChanges();
      this.positionSubscription = layoutEvents$.subscribe(() => {
        componentRef.instance.arrowSide = this.arrowSideTowardTarget(target);
        this.updateSpotlight();
        componentRef.changeDetectorRef.detectChanges();
      });
    }
  }

  private listenOutsideClick(): void {
    this.outsideClickSubscription?.unsubscribe();
    // Evita fechar no mesmo clique que abriu o guia.
    this.outsideClickSubscription = fromEvent<MouseEvent>(document, "mousedown", {
      capture: true,
    }).subscribe((event) => {
      const node = event.target as Node | null;
      if (!node) {
        return;
      }
      const onBalloon = !!this.overlayRef?.overlayElement?.contains(node);
      const onTarget = !!this.spotlightTarget && this.spotlightTarget.contains(node);
      if (!onBalloon && !onTarget) {
        this.guideService.finish("skipped");
      }
    });
  }

  private mountSpotlight(target: Element): void {
    this.disposeSpotlight();
    this.spotlightTarget = target;
    this.spotlightEl = document.createElement("div");
    this.spotlightEl.className = "guide-spotlight";
    this.spotlightEl.setAttribute("aria-hidden", "true");
    document.body.appendChild(this.spotlightEl);
    this.updateSpotlight();
  }

  private updateSpotlight(): void {
    if (!this.spotlightEl || !this.spotlightTarget) {
      return;
    }
    const rect = (this.spotlightTarget as HTMLElement).getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      this.spotlightEl.style.display = "none";
      return;
    }
    this.spotlightEl.style.display = "block";
    this.spotlightEl.style.top = `${Math.max(0, rect.top - SPOTLIGHT_PADDING_PX)}px`;
    this.spotlightEl.style.left = `${Math.max(0, rect.left - SPOTLIGHT_PADDING_PX)}px`;
    this.spotlightEl.style.width = `${rect.width + SPOTLIGHT_PADDING_PX * 2}px`;
    this.spotlightEl.style.height = `${rect.height + SPOTLIGHT_PADDING_PX * 2}px`;
  }

  private disposeSpotlight(): void {
    this.spotlightEl?.remove();
    this.spotlightEl = null;
    this.spotlightTarget = null;
  }

  private resolveArrowSide(pair: ConnectedPosition): GuideArrowSide {
    if (pair.originY === "bottom" && pair.overlayY === "top") {
      return "top";
    }
    if (pair.originY === "top" && pair.overlayY === "bottom") {
      return "bottom";
    }
    if (pair.originX === "end" && pair.overlayX === "start") {
      return "left";
    }
    if (pair.originX === "start" && pair.overlayX === "end") {
      return "right";
    }
    return "top";
  }

  /** Com balão centralizado, a seta aponta para o lado em que o alvo está. */
  private arrowSideTowardTarget(target: Element): GuideArrowSide {
    const rect = (target as HTMLElement).getBoundingClientRect();
    const targetCx = rect.left + rect.width / 2;
    const targetCy = rect.top + rect.height / 2;
    const viewCx = window.innerWidth / 2;
    const viewCy = window.innerHeight / 2;
    const dx = targetCx - viewCx;
    const dy = targetCy - viewCy;

    if (Math.abs(dx) > Math.abs(dy)) {
      return dx > 0 ? "right" : "left";
    }
    return dy > 0 ? "bottom" : "top";
  }

  private queryTarget(selector: string): Element | null {
    try {
      return document.querySelector(selector);
    } catch {
      return null;
    }
  }

  private disposeOverlay(): void {
    this.positionSubscription?.unsubscribe();
    this.positionSubscription = null;
    this.outsideClickSubscription?.unsubscribe();
    this.outsideClickSubscription = null;
    this.disposeSpotlight();
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
